<template>
    <div class="rich-editor-wrapper">
        <!-- Toolbar -->
        <div :id="toolbarId" class="rich-editor-toolbar">
            <span class="ql-formats">
                <button class="ql-bold"      title="Negrito"/>
                <button class="ql-italic"    title="Itálico"/>
                <button class="ql-underline" title="Sublinhado"/>
                <button class="ql-strike"    title="Tachado"/>
            </span>
            <span class="ql-formats">
                <select class="ql-header">
                    <option value="1">Título 1</option>
                    <option value="2">Título 2</option>
                    <option value="3">Título 3</option>
                    <option selected>Normal</option>
                </select>
            </span>
            <span class="ql-formats">
                <select class="ql-align">
                    <option selected/>
                    <option value="center"/>
                    <option value="right"/>
                    <option value="justify"/>
                </select>
            </span>
            <span class="ql-formats">
                <button class="ql-list" value="ordered" title="Lista numerada"/>
                <button class="ql-list" value="bullet"  title="Lista com marcadores"/>
            </span>
            <span class="ql-formats">
                <button class="ql-link"  title="Inserir link"/>
                <button class="ql-image" title="Inserir imagem ou GIF"/>
            </span>
            <span class="ql-formats">
                <button class="ql-clean" title="Remover formatação"/>
            </span>

            <!-- Botão com rota -->
            <span class="ql-formats">
                <button
                    class="ql-custom-btn"
                    @click.prevent="openBtnDialog"
                    title="Inserir botão com link de navegação"
                    type="button"
                >
                    <v-icon small>mdi-gesture-tap-button</v-icon>
                </button>
            </span>

            <!-- Toggle HTML -->
            <span class="ql-formats toolbar-extra">
                <button
                    class="ql-custom-btn"
                    :class="{ active: htmlMode }"
                    @click.prevent="toggleHtmlMode"
                    title="Alternar modo HTML"
                    type="button"
                >
                    <v-icon small>mdi-code-tags</v-icon>
                </button>
            </span>
        </div>

        <!-- Editor visual -->
        <div v-show="!htmlMode">
            <quill-editor
                :ref="editorRef"
                v-model="content"
                :options="editorOptions"
                class="rich-editor-content"
                @change="onChange"
                @ready="onEditorReady"
            />
        </div>

        <!-- Modo HTML bruto -->
        <div v-show="htmlMode" class="html-mode-container">
            <textarea
                v-model="htmlContent"
                class="html-textarea"
                spellcheck="false"
                @input="onHtmlInput"
            />
        </div>

        <!-- Input de arquivo oculto -->
        <input
            :ref="fileInputRef"
            type="file"
            accept="image/*,image/gif"
            style="display: none;"
            @change="onFileSelected"
        />

        <!-- Popover de redimensionamento de imagem -->
        <div
            v-if="resizePopover.visible"
            class="image-resize-popover"
            :style="{ top: resizePopover.top + 'px', left: resizePopover.left + 'px' }"
        >
            <span class="resize-label">Largura:</span>
            <button
                v-for="pct in [25, 50, 75, 100]"
                :key="pct"
                :class="['resize-btn', { active: resizePopover.currentWidth === pct + '%' }]"
                @click="applyImageWidth(pct + '%')"
            >{{ pct }}%</button>
            <input
                v-model="resizePopover.customWidth"
                class="resize-input"
                placeholder="ex: 300px"
                @keydown.enter="applyImageWidth(resizePopover.customWidth)"
                @click.stop
            />
            <button class="resize-btn" @click="applyImageWidth(resizePopover.customWidth)" title="Aplicar">
                <v-icon x-small>mdi-check</v-icon>
            </button>
            <button class="resize-btn resize-btn--close" @click="closeResizePopover" title="Fechar">
                <v-icon x-small>mdi-close</v-icon>
            </button>
        </div>

        <!-- Dialog: Botão com rota / link -->
        <v-dialog v-model="btnDialog.visible" max-width="460px">
            <v-card>
                <v-card-title class="text-subtitle-1">
                    <v-icon left>mdi-gesture-tap-button</v-icon>
                    Inserir Botão de Navegação
                </v-card-title>

                <v-card-text>
                    <!-- Texto do botão -->
                    <v-text-field
                        v-model="btnDialog.text"
                        label="Texto do botão"
                        placeholder="Ex: Ver Cardápio"
                        outlined
                        dense
                        class="mb-4"
                        hide-details
                        autofocus
                    />

                    <!-- Toggle rota / link -->
                    <div class="d-flex mb-3" style="gap: 8px;">
                        <v-btn
                            small depressed
                            :color="btnDialog.mode === 'route' ? 'primary' : ''"
                            :dark="btnDialog.mode === 'route'"
                            @click="btnDialog.mode = 'route'"
                        >
                            <v-icon small left>mdi-navigation</v-icon>
                            Rota interna
                        </v-btn>
                        <v-btn
                            small depressed
                            :color="btnDialog.mode === 'url' ? 'primary' : ''"
                            :dark="btnDialog.mode === 'url'"
                            @click="btnDialog.mode = 'url'"
                        >
                            <v-icon small left>mdi-open-in-new</v-icon>
                            Link externo
                        </v-btn>
                    </div>

                    <!-- Rota interna -->
                    <div v-if="btnDialog.mode === 'route'">
                        <v-text-field
                            v-model="btnDialog.path"
                            label="Rota de destino"
                            placeholder="Ex: /cardapio"
                            outlined
                            dense
                            hide-details
                            prepend-inner-icon="mdi-navigation"
                        />
                        <div class="mt-2 d-flex flex-wrap" style="gap: 4px;">
                            <v-chip
                                v-for="rota in rotasComuns"
                                :key="rota"
                                x-small
                                outlined
                                color="primary"
                                style="cursor: pointer;"
                                @click="btnDialog.path = rota"
                            >{{ rota }}</v-chip>
                        </div>
                    </div>

                    <!-- Link externo -->
                    <v-text-field
                        v-if="btnDialog.mode === 'url'"
                        v-model="btnDialog.url"
                        label="URL de destino"
                        placeholder="https://"
                        outlined
                        dense
                        hide-details
                        prepend-inner-icon="mdi-link"
                    />

                    <div class="mt-2 caption grey--text">
                        <v-icon x-small>mdi-information-outline</v-icon>
                        <span v-if="btnDialog.mode === 'route'">Navega dentro do app (router.push).</span>
                        <span v-else>Abre o link em uma nova aba do navegador.</span>
                    </div>

                    <!-- Preview do botão -->
                    <div v-if="btnDialog.text" class="mt-4 text-center">
                        <button class="novidade-router-btn novidade-router-btn--preview" type="button">
                            <span class="novidade-router-btn__text">{{ btnDialog.text }}</span>
                            <span class="novidade-router-btn__badge">
                                <template v-if="btnDialog.mode === 'route'">&rarr; {{ btnDialog.path || '/rota' }}</template>
                                <template v-else>&nearr; {{ btnDialog.url || 'https://...' }}</template>
                            </span>
                        </button>
                    </div>
                </v-card-text>

                <v-card-actions>
                    <v-btn text @click="btnDialog.visible = false">Cancelar</v-btn>
                    <v-spacer/>
                    <v-btn
                        color="primary"
                        :disabled="!btnDialog.text || (btnDialog.mode === 'route' ? !btnDialog.path : !btnDialog.url)"
                        @click="confirmRouterButton"
                    >
                        <v-icon left>mdi-check</v-icon>
                        Inserir
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Dialog de upload de imagem -->
        <v-dialog v-model="imageDialog" max-width="480px" @click:outside="cancelImage">
            <v-card>
                <v-card-title class="text-subtitle-1">
                    <v-icon left>mdi-image-plus</v-icon>
                    Inserir Imagem / GIF
                </v-card-title>

                <v-card-text>
                    <div
                        class="upload-dropzone"
                        :class="{ 'drag-over': isDragging }"
                        @click="triggerFileInput"
                        @dragover.prevent="isDragging = true"
                        @dragleave.prevent="isDragging = false"
                        @drop.prevent="onDrop"
                    >
                        <div v-if="previewUrl" class="preview-container">
                            <img :src="previewUrl" alt="preview" class="upload-preview" />
                            <div v-if="uploading" class="upload-overlay">
                                <v-progress-circular indeterminate color="primary" size="36"/>
                            </div>
                            <v-icon v-if="!uploading" class="preview-change-hint" color="white" size="20">
                                mdi-pencil
                            </v-icon>
                        </div>

                        <div v-else class="text-center grey--text pa-6">
                            <v-icon large color="grey lighten-1">mdi-image-plus</v-icon>
                            <p class="caption mt-2 mb-0">Clique ou arraste a imagem / GIF aqui</p>
                            <p class="caption grey--text text--lighten-1 mb-0">PNG, JPG, GIF, WebP</p>
                        </div>
                    </div>

                    <div v-if="uploadError" class="error--text caption mt-2">
                        <v-icon small color="error">mdi-alert</v-icon>
                        {{ uploadError }}
                    </div>
                </v-card-text>

                <v-card-actions>
                    <v-btn text @click="cancelImage">Cancelar</v-btn>
                    <v-spacer/>
                    <v-btn
                        color="primary"
                        :loading="uploading"
                        :disabled="!tmpPath || uploading"
                        @click="confirmImage"
                    >
                        <v-icon left>mdi-check</v-icon>
                        Inserir
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import Quill from 'quill';
import { quillEditor } from 'vue-quill-editor';
import 'quill/dist/quill.snow.css';

// ── Blot customizado: ensina o Quill a preservar o atributo style em imagens
// Sem isso, o MutationObserver interno do Quill reverte qualquer mudanca
// de estilo feita diretamente no DOM.
const ImageBlotBase = Quill.import('formats/image');

class ResizableImageBlot extends ImageBlotBase {
    static formats(domNode) {
        const formats = super.formats(domNode);
        if (domNode.hasAttribute('style')) {
            formats.style = domNode.getAttribute('style');
        }
        return formats;
    }

    format(name, value) {
        if (name === 'style') {
            if (value) {
                this.domNode.setAttribute('style', value);
            } else {
                this.domNode.removeAttribute('style');
            }
        } else {
            super.format(name, value);
        }
    }
}
ResizableImageBlot.blotName = 'image';
ResizableImageBlot.tagName  = 'IMG';
Quill.register(ResizableImageBlot, true);

// ── Blot: Botão com navegação (rota interna OU link externo) ──────────────────
const BlockEmbed = Quill.import('blots/block/embed');

class RouterButtonBlot extends BlockEmbed {
    static create(value) {
        const node = super.create();
        node.setAttribute('contenteditable', 'false');
        node.setAttribute('type', 'button');
        node.className = 'novidade-router-btn';

        if (value.mode === 'url') {
            node.setAttribute('data-external-url', value.url || '');
            node.setAttribute('data-btn-mode', 'url');
        } else {
            node.setAttribute('data-router-push', value.path || '');
            node.setAttribute('data-btn-mode', 'route');
        }

        const badgeIcon = value.mode === 'url' ? '⇗' : '→';
        const badgeText = value.mode === 'url' ? (value.url || '') : (value.path || '');

        node.innerHTML =
            `<span class="novidade-router-btn__text">${value.text}</span>` +
            `<span class="novidade-router-btn__badge">${badgeIcon} ${badgeText}</span>`;
        return node;
    }

    static value(node) {
        const mode = node.getAttribute('data-btn-mode') || 'route';
        return {
            text: node.querySelector('.novidade-router-btn__text')?.textContent || node.textContent,
            mode,
            path: node.getAttribute('data-router-push') || '',
            url:  node.getAttribute('data-external-url') || '',
        };
    }
}
RouterButtonBlot.blotName = 'router-button';
RouterButtonBlot.tagName  = 'BUTTON';
Quill.register(RouterButtonBlot, true);

let editorCounter = 0;

export default {
    name: 'RichTextEditor',

    components: { quillEditor },

    props: {
        value: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: 'Escreva o conteúdo da novidade...',
        },
    },

    data() {
        editorCounter++;
        const id = editorCounter;
        return {
            toolbarId:    `rich-editor-toolbar-${id}`,
            editorRef:    `quillEditor${id}`,
            fileInputRef: `fileInput${id}`,

            content:     this.value || '',
            htmlContent: '',       // variável exclusiva do textarea — isolada do Quill
            htmlMode:    false,

            // Dialog: botão de navegação
            btnDialog: {
                visible: false,
                text:    '',
                mode:    'route',   // 'route' | 'url'
                path:    '',
                url:     '',
            },

            // Rotas comuns do zaper-comandas
            rotasComuns: [
                '/',
                '/cardapio',
                '/pedidos',
                '/caixa',
                '/relatorios',
                '/novidades',
                '/configuracoes',
            ],

            // Upload
            imageDialog: false,
            previewUrl:  null,
            tmpPath:     null,
            uploading:   false,
            isDragging:  false,
            uploadError: null,

            // Resize popover
            resizePopover: {
                visible:      false,
                top:          0,
                left:         0,
                currentWidth: '',
                customWidth:  '',
            },

            editorOptions: {
                theme: 'snow',
                placeholder: this.placeholder,
                modules: {
                    toolbar: {
                        container: `#rich-editor-toolbar-${id}`,
                        handlers: {
                            image: this.openImageDialog,

                            // Handler customizado: aplica heading apenas no bloco
                            // onde o cursor está, não em todos os blocos selecionados
                            header: (value) => {
                                const quill = this.$refs[this.editorRef]?.quill;
                                if (!quill) return;
                                const sel = quill.getSelection();
                                if (sel === null) return;
                                quill.formatLine(
                                    sel.index,   // posição do cursor/início da seleção
                                    1,           // comprimento 1 = apenas esta linha
                                    'header',
                                    value || false,
                                    'user'
                                );
                            },
                        },
                    },
                },
            },
        };
    },

    watch: {
        value(newVal) {
            if (!this.htmlMode && newVal !== this.content) {
                this.content = newVal || '';
            }
        },
    },

    mounted() {
        // Fecha popover ao clicar fora
        document.addEventListener('click', this.onDocumentClick);
    },

    created() {
        // DOM element armazenado fora do sistema reativo do Vue
        // para evitar que o proxy do Vue2 corrumpa a referência
        this._targetImg = null;
    },

    beforeDestroy() {
        document.removeEventListener('click', this.onDocumentClick);
    },

    methods: {
        // ── Editor events ─────────────────────────────────────────────────────
        onChange({ html }) {
            // Guarda em content E emite para o pai
            const cleaned = (html === '<p><br></p>') ? '' : html;
            this.content = cleaned;
            this.$emit('input', cleaned);
        },

        onHtmlInput() {
            // Textarea editou o HTML bruto — propaga ao pai
            this.$emit('input', this.htmlContent);
        },

        onEditorReady(quill) {
            // Bloqueia seleção geral ao clicar em imagem (bug nativo do contenteditable)
            quill.root.addEventListener('mousedown', (e) => {
                if (e.target.tagName === 'IMG') {
                    e.preventDefault();
                }
            });

            // Click na imagem: abre popover e impede bubbling para o document
            quill.root.addEventListener('click', (e) => {
                if (e.target.tagName === 'IMG') {
                    e.stopPropagation();
                    this.openResizePopover(e.target);
                } else {
                    this.closeResizePopover();
                }
            });
        },

        // ── Toggle HTML ───────────────────────────────────────────────────────
        toggleHtmlMode() {
            this.closeResizePopover();
            this.htmlMode = !this.htmlMode;

            if (this.htmlMode) {
                // Ao entrar: pega HTML atual do Quill, formata e coloca no textarea
                const quill = this.$refs[this.editorRef]?.quill;
                const raw = quill ? quill.root.innerHTML : this.content;
                this.htmlContent = this.formatHtml(raw === '<p><br></p>' ? '' : raw);
            } else {
                // Ao sair: passa o HTML do textarea (possivelmente editado) pro Quill
                this.content = this.htmlContent;
                this.$nextTick(() => {
                    const quill = this.$refs[this.editorRef]?.quill;
                    if (quill) {
                        quill.clipboard.dangerouslyPasteHTML(this.htmlContent || '');
                    }
                });
            }
        },

        // ── Formatador de HTML ────────────────────────────────────────────────
        formatHtml(html) {
            if (!html) return '';

            const VOID_TAGS   = new Set(['area','base','br','col','embed','hr','img','input','link','meta','param','source','track','wbr']);
            const INLINE_TAGS = new Set(['a','abbr','b','bdi','bdo','cite','code','data','dfn','em','i','kbd','mark','q','s','samp','small','span','strong','sub','sup','time','u','var','wbr']);

            const TAB = '  ';
            let depth  = 0;
            let output = '';

            // Tokeniza: tags e texto
            const tokens = [];
            const rgx = /<[^>]+>|[^<]+/g;
            let m;
            while ((m = rgx.exec(html)) !== null) {
                const t = m[0].trim();
                if (t) tokens.push(t);
            }

            tokens.forEach(token => {
                if (token.startsWith('</')) {
                    // Tag de fechamento
                    const tag = (token.match(/<\/(\w+)/) || [])[1]?.toLowerCase();
                    if (INLINE_TAGS.has(tag)) {
                        output += token;
                    } else {
                        depth = Math.max(0, depth - 1);
                        // Se a última coisa no output não for newline, adiciona
                        if (!output.endsWith('\n')) output += '\n';
                        output += TAB.repeat(depth) + token + '\n';
                    }
                } else if (token.startsWith('<')) {
                    // Tag de abertura ou auto-fechamento
                    const tag = (token.match(/<(\w+)/) || [])[1]?.toLowerCase();
                    const isSelfClose = VOID_TAGS.has(tag) || token.endsWith('/>');
                    const isInline    = INLINE_TAGS.has(tag);

                    if (isInline) {
                        output += token;
                    } else {
                        if (output && !output.endsWith('\n')) output += '\n';
                        output += TAB.repeat(depth) + token + '\n';
                        if (!isSelfClose) depth++;
                    }
                } else {
                    // Nó de texto
                    output += token;
                }
            });

            return output.trim();
        },

        // ── Resize de imagem ──────────────────────────────────────────────────
        openResizePopover(imgEl) {
            // Salva referência fora do Vue reativo
            this._targetImg = imgEl;

            const editorContainer = this.$refs[this.editorRef]?.$el;
            if (!editorContainer) return;

            const editorRect = editorContainer.getBoundingClientRect();
            const imgRect    = imgEl.getBoundingClientRect();

            const top  = imgRect.bottom - editorRect.top + 4;
            const left = imgRect.left   - editorRect.left;

            this.resizePopover = {
                visible:      true,
                top,
                left:         Math.max(0, left),
                currentWidth: imgEl.style.width || imgEl.getAttribute('width') || '',
                customWidth:  '',
            };
        },

        applyImageWidth(width) {
            if (!width || !this._targetImg) return;

            const quill = this.$refs[this.editorRef]?.quill;
            if (!quill) return;

            // Usa a API do Quill (blot.format) para que o MutationObserver
            // interno reconheça a mudança e não a reverta
            const blot = Quill.find(this._targetImg);
            if (blot) {
                blot.format('style', `width: ${width}; max-width: 100%;`);
            } else {
                // Fallback direto no DOM (ex: imagem fora de blot)
                this._targetImg.setAttribute('style', `width: ${width}; max-width: 100%;`);
            }

            this.resizePopover.currentWidth = width;

            // Sincroniza content e emite
            const html = quill.root.innerHTML;
            this.content = html;
            this.$emit('input', html);
        },

        closeResizePopover() {
            this.resizePopover.visible = false;
            this._targetImg = null;
        },

        onDocumentClick(e) {
            if (!this.resizePopover.visible) return;
            const popoverEl = this.$el && this.$el.querySelector('.image-resize-popover');
            if (popoverEl && !popoverEl.contains(e.target)) {
                this.closeResizePopover();
            }
        },

        // ── Botão de navegação ─────────────────────────────────────────────────────
        openBtnDialog() {
            this.btnDialog = { visible: true, text: '', mode: 'route', path: '', url: '' };
        },

        confirmRouterButton() {
            const { text, mode, path, url } = this.btnDialog;
            if (!text) return;
            if (mode === 'route' && !path) return;
            if (mode === 'url'   && !url)  return;

            const quill = this.$refs[this.editorRef]?.quill;
            if (!quill) return;

            const range = quill.getSelection(true) || { index: quill.getLength() - 1 };
            quill.insertEmbed(range.index, 'router-button', { text, mode, path, url }, 'user');
            quill.setSelection(range.index + 1, 0, 'silent');

            // Sincroniza content
            this.$nextTick(() => {
                const html = quill.root.innerHTML;
                this.content = html;
                this.$emit('input', html);
            });

            this.btnDialog.visible = false;
        },

        // ── Upload de imagem ──────────────────────────────────────────────────
        openImageDialog() {
            this.previewUrl  = null;
            this.tmpPath     = null;
            this.uploading   = false;
            this.isDragging  = false;
            this.uploadError = null;
            this.imageDialog = true;
        },

        cancelImage() {
            this.imageDialog = false;
        },

        confirmImage() {
            if (!this.tmpPath) return;

            const quill = this.$refs[this.editorRef].quill;
            const range = quill.getSelection(true);
            const url   = `${this.$urlAPI}/files/tmp/${this.tmpPath}`;

            quill.insertEmbed(range.index, 'image', url);
            quill.setSelection(range.index + 1);

            this.imageDialog = false;
        },

        triggerFileInput() {
            this.$refs[this.fileInputRef].click();
        },

        onFileSelected(e) {
            const file = e.target.files[0];
            if (file) this.uploadFile(file);
            e.target.value = '';
        },

        onDrop(e) {
            this.isDragging = false;
            const file = Array.from(e.dataTransfer.files).find(f => f.type.startsWith('image/'));
            if (file) this.uploadFile(file);
        },

        async uploadFile(file) {
            this.uploadError = null;
            this.uploading   = true;

            const reader = new FileReader();
            reader.onload = ev => (this.previewUrl = ev.target.result);
            reader.readAsDataURL(file);

            try {
                const formData = new FormData();
                formData.append('file', file);

                const resp = await this.$http.post('files/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });

                this.tmpPath = resp.data.path;
            } catch {
                this.uploadError = 'Falha ao enviar o arquivo. Tente novamente.';
                this.previewUrl  = null;
                this.tmpPath     = null;
            } finally {
                this.uploading = false;
            }
        },
    },
};
</script>

<style>
/* Wrapper */
.rich-editor-wrapper {
    border: 1px solid rgba(0, 0, 0, 0.38);
    border-radius: 4px;
    overflow: visible; /* necessário para o popover sair da bounding box */
    transition: border-color 0.2s;
    position: relative;
}

.rich-editor-wrapper:focus-within {
    border-color: var(--v-primary-base, #1976d2);
    border-width: 2px;
}

/* Toolbar Quill */
.rich-editor-wrapper .ql-toolbar.ql-snow {
    border: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    background: #fafafa;
    padding: 6px 8px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
}

.rich-editor-wrapper .ql-container.ql-snow {
    border: none;
    font-size: 14px;
}

/* Botões extras na toolbar */
.toolbar-extra {
    margin-left: auto;
}

.ql-custom-btn {
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 1px solid transparent;
    border-radius: 4px;
    background: transparent;
    cursor: pointer;
    color: #444;
    transition: background 0.15s, border-color 0.15s;
}

.ql-custom-btn:hover {
    background: rgba(0, 0, 0, 0.08);
    border-color: rgba(0, 0, 0, 0.15);
}

.ql-custom-btn.active {
    background: rgba(25, 118, 210, 0.12);
    border-color: var(--v-primary-base, #1976d2);
    color: var(--v-primary-base, #1976d2);
}

/* Área de edição */
.rich-editor-content .ql-editor {
    min-height: 200px;
    max-height: 500px;
    overflow-y: auto;
    line-height: 1.6;
}

.rich-editor-content .ql-editor img {
    max-width: 100%;
    border-radius: 2px;
    display: block;
    margin: 6px auto;
    cursor: pointer;
}

.rich-editor-content .ql-editor img:hover {
    outline: 2px solid var(--v-primary-base, #1976d2);
    outline-offset: 2px;
}

/* Modo HTML */
.html-mode-container {
    background: #1e1e1e;
}

.html-textarea {
    width: 100%;
    min-height: 240px;
    max-height: 500px;
    padding: 14px;
    font-family: 'Fira Code', 'Consolas', 'Courier New', monospace;
    font-size: 13px;
    line-height: 1.6;
    color: #d4d4d4;
    background: #1e1e1e;
    border: none;
    resize: vertical;
    box-sizing: border-box;
    outline: none;
}

/* Popover de resize de imagem */
.image-resize-popover {
    position: absolute;
    z-index: 999;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 5px 8px;
    background: #fff;
    border: 1px solid rgba(0,0,0,0.15);
    border-radius: 6px;
    box-shadow: 0 3px 12px rgba(0,0,0,0.15);
    flex-wrap: nowrap;
    white-space: nowrap;
}

.resize-label {
    font-size: 11px;
    color: #666;
    font-weight: 500;
    margin-right: 2px;
}

.resize-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 36px;
    height: 26px;
    padding: 0 6px;
    font-size: 11px;
    font-weight: 500;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: #f5f5f5;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s, color 0.15s;
    color: #333;
}

.resize-btn:hover {
    background: #e3f2fd;
    border-color: var(--v-primary-base, #1976d2);
    color: var(--v-primary-base, #1976d2);
}

.resize-btn.active {
    background: var(--v-primary-base, #1976d2);
    border-color: var(--v-primary-base, #1976d2);
    color: #fff;
}

.resize-btn--close:hover {
    background: #fde8e8;
    border-color: #e53935;
    color: #e53935;
}

.resize-input {
    height: 26px;
    width: 72px;
    padding: 0 6px;
    font-size: 11px;
    border: 1px solid #ddd;
    border-radius: 4px;
    outline: none;
    transition: border-color 0.15s;
}

.resize-input:focus {
    border-color: var(--v-primary-base, #1976d2);
}

/* Dropzone */
.upload-dropzone {
    border: 2px dashed #bbb;
    border-radius: 8px;
    cursor: pointer;
    min-height: 140px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: border-color 0.2s, background 0.2s;
    overflow: hidden;
    position: relative;
}

.upload-dropzone:hover,
.upload-dropzone.drag-over {
    border-color: var(--v-primary-base, #1976d2);
    background: rgba(25, 118, 210, 0.04);
}

.preview-container {
    position: relative;
    width: 100%;
    text-align: center;
}

.upload-preview {
    max-width: 100%;
    max-height: 200px;
    border-radius: 4px;
    display: block;
    margin: 0 auto;
    padding: 8px;
}

.upload-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.75);
    border-radius: 8px;
}

.preview-change-hint {
    position: absolute;
    bottom: 12px;
    right: 12px;
    background: rgba(0,0,0,0.45);
    border-radius: 50%;
    padding: 4px;
}
/* ── Botão de navegação (dentro do editor e no preview do dialog) ────────── */
.novidade-router-btn {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 9px 18px;
    background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
    color: #fff !important;
    border: none;
    border-radius: 8px;
    cursor: default;
    font-size: 14px;
    font-weight: 500;
    margin: 10px 0;
    box-shadow: 0 2px 8px rgba(21, 101, 192, 0.35);
    user-select: none;
    font-family: inherit;
    letter-spacing: 0.3px;
}

.novidade-router-btn__text {
    font-size: 14px;
    font-weight: 600;
    color: #fff;
    pointer-events: none;
}

.novidade-router-btn__badge {
    font-size: 10px;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.8);
    background: rgba(255, 255, 255, 0.18);
    padding: 2px 8px;
    border-radius: 20px;
    letter-spacing: 0.2px;
    pointer-events: none;
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Preview no dialog - cursor pointer para parecer clicavel */
.novidade-router-btn--preview {
    cursor: pointer;
    transition: box-shadow 0.2s, transform 0.15s;
}
.novidade-router-btn--preview:hover {
    box-shadow: 0 4px 14px rgba(21, 101, 192, 0.5);
    transform: translateY(-1px);
}

/* Estilo especifico para dentro do ql-editor */
.ql-editor .novidade-router-btn {
    display: inline-flex;
}
</style>
